/**
 * SKD-2314 — PostHog product analytics for the portal.
 *
 * Same PostHog project as www.skript.com.au (skript-website26/js/main.js), so a visitor who
 * clicks through from the website continues as the same person and keeps their first-touch
 * UTM tags. The privacy policy was updated for this first (SKD-2313, live 23 Sep 2026); what
 * it promises is what this module enforces:
 *
 * - no banking/CDR data, no names, phone numbers or payment details — only email and the
 *   organisation IDs identify a person;
 * - page addresses are sent as route patterns (`/customers/[id]`), never with IDs, and with
 *   no query string except `utm_*` (the password-set email link carries a token in its query);
 * - no autocapture, no session recording, heatmaps, surveys or other remote extensions.
 *
 * Only the production portal sends anything. Everywhere else (local dev, the pactified
 * Cypress server, CI) nothing is initialised. `NEXT_PUBLIC_ANALYTICS_DEBUG=true` turns it on
 * locally in a dry-run mode that logs each finished event to the console (and keeps them in
 * `window.__analyticsDryRun`) and sends nothing.
 */
import posthog, { type CaptureResult, type Properties } from 'posthog-js';

// Publishable project key — the same one the public website ships in its JavaScript.
const POSTHOG_KEY = 'phc_n9TxWmYBmp8Pt4tDLfKv82u4ndb5MhDvFyYkx3Epj9E5';
const POSTHOG_HOST = 'https://us.i.posthog.com';
export const PRODUCTION_HOST = 'portal.skript.com.au';
// Dry-run mode points at a closed local port so nothing could leave even if a send slipped through.
const DEBUG_HOST = 'http://127.0.0.1:9';

const INTERNAL_EMAIL_DOMAINS = ['@skript.com.au', '@nukaizen.com.au'];
const ID_PLACEHOLDER = '[id]';

export type AnalyticsMode = 'off' | 'live' | 'debug';

export function analyticsMode(hostname: string, debugFlag: string | undefined): AnalyticsMode {
  if (debugFlag === 'true') return 'debug';
  return hostname === PRODUCTION_HOST ? 'live' : 'off';
}

// ---------------------------------------------------------------------------------------------
// URL sanitising (pure, unit-tested)
// ---------------------------------------------------------------------------------------------

type RouteMatcher = { pattern: string; regex: RegExp };

/**
 * Turns Next's page list (`/customers/[id]`, `/api/sec/[[...action]]`) into matchers. The
 * build manifest lists pages most-specific first, so the first match wins.
 */
export function buildRouteMatchers(pages: string[]): RouteMatcher[] {
  return pages
    .filter((p) => !p.startsWith('/_') && !p.startsWith('/api/'))
    .map((pattern) => {
      const segments = pattern.split('/').filter(Boolean);
      const body = segments
        .map((seg) => {
          if (/^\[\[\.\.\..+\]\]$/.test(seg)) return '(?:/.*)?';
          if (/^\[\.\.\..+\]$/.test(seg)) return '/.+';
          if (/^\[.+\]$/.test(seg)) return '/[^/]+';
          return `/${seg.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`;
        })
        .join('');
      return { pattern, regex: new RegExp(`^${body || '/'}/?$`) };
    });
}

const LOOKS_LIKE_ID = [
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, // UUID
  /^\d+$/, // numeric id
  /^(?=.*\d)[A-Za-z0-9_-]{16,}$/, // long opaque token with digits
];

/** Last resort when no route matches: blank out anything that looks like an identifier. */
export function redactIdSegments(path: string): string {
  return path
    .split('/')
    .map((seg) => (seg && LOOKS_LIKE_ID.some((re) => re.test(seg)) ? ID_PLACEHOLDER : seg))
    .join('/');
}

export function routePatternFor(path: string, matchers: RouteMatcher[]): string {
  const clean = path.split(/[?#]/)[0] || '/';
  const hit = matchers.find((m) => m.regex.test(clean));
  return hit ? hit.pattern : redactIdSegments(clean);
}

function utmOnly(search: string): string {
  const kept = new URLSearchParams();
  new URLSearchParams(search).forEach((value, key) => {
    if (key.toLowerCase().startsWith('utm_')) kept.append(key, value);
  });
  const qs = kept.toString();
  return qs ? `?${qs}` : '';
}

/**
 * Portal URLs keep their origin, get the route pattern for a path and lose every query
 * parameter except utm_*. Other sites' URLs (referrers) keep their path but lose everything
 * except utm_* too. The fragment is always dropped. Non-URLs come back unchanged.
 */
export function sanitiseUrl(value: string, portalHosts: string[], matchers: RouteMatcher[]): string {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return value;
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return value;
  const path = portalHosts.includes(url.hostname) ? routePatternFor(url.pathname, matchers) : url.pathname;
  return `${url.origin}${path}${utmOnly(url.search)}`;
}

const DROPPED_KEYS = new Set(['title', '$title']);

function isPathKey(key: string): boolean {
  return /pathname$/i.test(key);
}

function isUrlKey(key: string): boolean {
  return /(url|referrer)$/i.test(key);
}

/**
 * Applied to properties, $set and $set_once of every event. Handles the obvious keys
 * ($current_url, $pathname, $referrer, $initial_current_url, $session_entry_url, ...) and the
 * nested $initial_person_info / $initial_referrer_info objects PostHog keeps for first touch.
 */
export function sanitiseProperties(
  props: Properties | undefined,
  portalHosts: string[],
  matchers: RouteMatcher[]
): Properties | undefined {
  if (props === undefined || props === null) return props;
  const out: Properties = {};
  Object.entries(props).forEach(([key, value]) => {
    if (DROPPED_KEYS.has(key)) return;
    if (typeof value === 'string') {
      if (isPathKey(key)) {
        out[key] = routePatternFor(value, matchers);
      } else if (isUrlKey(key) || key === 'u' || key === 'r') {
        // `u`/`r` are the url/referrer inside $initial_person_info
        out[key] = sanitiseUrl(value, portalHosts, matchers);
      } else {
        out[key] = value;
      }
    } else if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      out[key] = sanitiseProperties(value as Properties, portalHosts, matchers);
    } else {
      out[key] = value;
    }
  });
  return out;
}

export function sanitiseEvent(
  event: CaptureResult | null,
  portalHosts: string[],
  matchers: RouteMatcher[]
): CaptureResult | null {
  if (event === null) return null;
  return {
    ...event,
    properties: sanitiseProperties(event.properties, portalHosts, matchers) ?? {},
    $set: sanitiseProperties(event.$set, portalHosts, matchers),
    $set_once: sanitiseProperties(event.$set_once, portalHosts, matchers),
  };
}

export function isInternalEmail(email: string | null | undefined): boolean {
  if (!email) return false;
  const lower = email.toLowerCase();
  return INTERNAL_EMAIL_DOMAINS.some((d) => lower.endsWith(d));
}

// ---------------------------------------------------------------------------------------------
// Funnel events (SKD-2315) — names are fixed: the PostHog dashboards (SKD-2316) depend on them
// ---------------------------------------------------------------------------------------------

export type AnalyticsEvent =
  // account funnel (public pages; sandbox-only accounts count)
  | 'account_created'
  | 'email_verified'
  | 'password_set'
  | 'two_factor_set_up'
  | 'login_completed'
  // sandbox activity (both views; `view` says which)
  | 'view_switched'
  | 'credentials_created'
  | 'consent_link_created'
  | 'webhook_endpoint_created'
  | 'docs_opened'
  // paid funnel
  | 'pricing_viewed'
  | 'paid_signup_step_completed'
  | 'paid_signup_completed'
  | 'paid_signup_failed';

type EventProps = Record<string, string | number | boolean | undefined>;

/**
 * Keys that must never reach PostHog, whatever a call site passes: secrets, contact and
 * business details, payment details. Matched case-insensitively as a substring, so
 * `clientSecret`, `customerName`, `cardLast4` and `abnNumber` are all caught.
 */
const FORBIDDEN_KEY_PARTS = [
  'secret',
  'password',
  'token',
  'code',
  'name',
  'phone',
  'mobile',
  'email',
  'abn',
  'address',
  'card',
  'amount',
  'url',
];

export function isForbiddenKey(key: string): boolean {
  const lower = key.toLowerCase();
  return FORBIDDEN_KEY_PARTS.some((part) => lower.includes(part));
}

/** Drops forbidden keys and undefined values. Returns the dropped keys so they can be logged. */
export function safeEventProps(props: EventProps | undefined): { props: Properties; dropped: string[] } {
  const out: Properties = {};
  const dropped: string[] = [];
  Object.entries(props ?? {}).forEach(([key, value]) => {
    if (isForbiddenKey(key)) {
      dropped.push(key);
    } else if (value !== undefined) {
      out[key] = value;
    }
  });
  return { props: out, dropped };
}

const DOCS_PAGES: Record<string, string> = {
  '/documentation': 'documentation',
  '/openapi': 'openapi',
  '/postman': 'postman',
};

export function docsPageFor(pattern: string): string | undefined {
  return DOCS_PAGES[pattern];
}

export type PricingEntry = 'nav' | 'banner' | 'chip' | 'signup_flow' | 'direct';
const PRICING_ENTRY_KEY = 'skript-pricing-entry';

// ---------------------------------------------------------------------------------------------
// Runtime (browser only)
// ---------------------------------------------------------------------------------------------

let mode: AnalyticsMode = 'off';
let matchers: RouteMatcher[] = [];
let identifiedAs: string | undefined;
let pendingFacts: Properties = {};
// The shell mounts on every page, and most portal links are full page loads, so the last
// facts sent are remembered for the tab: person properties are only sent when they change.
const FACTS_SENT_KEY = 'skript-analytics-facts';

function readSentFacts(): string {
  try {
    return window.sessionStorage.getItem(FACTS_SENT_KEY) ?? '';
  } catch {
    return '';
  }
}

function writeSentFacts(value: string): void {
  try {
    if (value === '') window.sessionStorage.removeItem(FACTS_SENT_KEY);
    else window.sessionStorage.setItem(FACTS_SENT_KEY, value);
  } catch {
    // storage blocked: facts are re-sent on each page, which is harmless
  }
}

function sendFactsIfChanged(props: Properties): void {
  const key = JSON.stringify(props);
  if (key === readSentFacts()) return;
  writeSentFacts(key);
  posthog.setPersonProperties(props);
}
// Added to each event in before_send rather than registered as a super property, because
// PostHog persists super properties in the cookie shared with the website.
let currentView: 'test' | 'live' | undefined;

function pagesFromBuildManifest(): string[] {
  const manifest = (window as unknown as { __BUILD_MANIFEST?: { sortedPages?: string[] } }).__BUILD_MANIFEST;
  return manifest?.sortedPages ?? [];
}

export function analyticsEnabled(): boolean {
  return mode !== 'off';
}

/** Call once in the browser. Safe to call again; later calls do nothing. */
export function initAnalytics(): void {
  if (typeof window === 'undefined' || analyticsEnabled()) return;
  const next = analyticsMode(window.location.hostname, process.env.NEXT_PUBLIC_ANALYTICS_DEBUG);
  if (next === 'off') return;
  mode = next;
  matchers = buildRouteMatchers(pagesFromBuildManifest());
  const portalHosts = [PRODUCTION_HOST, window.location.hostname];

  posthog.init(POSTHOG_KEY, {
    api_host: mode === 'debug' ? DEBUG_HOST : POSTHOG_HOST,
    defaults: '2025-05-24',
    person_profiles: 'identified_only',
    cross_subdomain_cookie: true, // shared with www.skript.com.au: one person across both
    // Page views are sent by hand with the route pattern (see capturePageview).
    capture_pageview: false,
    capture_pageleave: false,
    // Only explicit events: autocapture would pick up button and table text on data pages.
    autocapture: false,
    rageclick: false,
    capture_dead_clicks: false,
    capture_exceptions: false,
    capture_performance: false,
    enable_heatmaps: false,
    disable_session_recording: true, // SKD-2317 may enable it for public sign-up pages only
    disable_surveys: true,
    disable_web_experiments: true,
    disable_external_dependency_loading: true,
    advanced_disable_flags: true,
    before_send: (event) => {
      const clean = sanitiseEvent(event, portalHosts, matchers);
      if (clean !== null && currentView !== undefined) clean.properties.view = currentView;
      if (mode === 'debug') {
        // eslint-disable-next-line no-console
        console.info('[analytics dry-run]', clean?.event, JSON.stringify(clean));
        const w = window as unknown as { __analyticsDryRun?: Array<CaptureResult | null> };
        (w.__analyticsDryRun ??= []).push(clean);
        return null;
      }
      return clean;
    },
  });
}

let firstPageviewSent = false;

/**
 * The page view for the page the app loaded on. Sent once per load even if the effect that
 * calls it runs twice (React strict mode does that in development).
 */
export function captureFirstPageview(pattern: string): void {
  if (firstPageviewSent) return;
  firstPageviewSent = true;
  // Deferred a tick so the portal shell's effect has set the view (test/live) first.
  setTimeout(() => capturePageview(pattern), 0);
}

/** `pattern` is Next's router.pathname, e.g. `/customers/[id]`. */
export function capturePageview(pattern: string): void {
  if (!analyticsEnabled()) return;
  posthog.capture('$pageview', {
    $current_url: `${window.location.origin}${pattern}${utmOnly(window.location.search)}`,
    $pathname: pattern,
  });
  const docsPage = docsPageFor(pattern);
  if (docsPage !== undefined) track('docs_opened', { page: docsPage });
}

/**
 * SKD-2315 — one funnel event. Fire it after the matching API call succeeds, never on the
 * click alone. Does nothing unless analytics is on, so call sites need no guards and tests no
 * mocks. Forbidden keys are dropped here as a last line of defence.
 */
export function track(event: AnalyticsEvent, props?: EventProps): void {
  if (!analyticsEnabled()) return;
  const { props: safe, dropped } = safeEventProps(props);
  if (dropped.length > 0) {
    // eslint-disable-next-line no-console
    console.warn(`[analytics] dropped forbidden properties from ${event}: ${dropped.join(', ')}`);
  }
  posthog.capture(event, safe);
}

/**
 * Remembers which link led to the pricing page, for `pricing_viewed`. Session storage rather
 * than memory because the header's Upgrade chip is a plain link (a full page load).
 */
export function notePricingEntry(entry: PricingEntry): void {
  try {
    window.sessionStorage.setItem(PRICING_ENTRY_KEY, entry);
  } catch {
    // storage blocked (private mode): the view is recorded as 'direct'
  }
}

/** Reads and clears the remembered entry point; 'direct' when there is none. */
export function takePricingEntry(): PricingEntry {
  try {
    const entry = window.sessionStorage.getItem(PRICING_ENTRY_KEY) as PricingEntry | null;
    window.sessionStorage.removeItem(PRICING_ENTRY_KEY);
    return entry ?? 'direct';
  } catch {
    return 'direct';
  }
}

export function identifyUser(userId: string, email: string | null | undefined): void {
  if (!analyticsEnabled() || !userId || identifiedAs === userId) return;
  identifiedAs = userId;
  if (posthog.get_distinct_id() === userId) {
    // Already identified on an earlier page load: calling identify again would only re-send
    // the same person properties as an extra event on every page.
    if (Object.keys(pendingFacts).length > 0) sendFactsIfChanged(pendingFacts);
    pendingFacts = {};
    return;
  }
  if (Object.keys(pendingFacts).length > 0) writeSentFacts(JSON.stringify(pendingFacts));
  posthog.identify(userId, { email: email ?? undefined, internal: isInternalEmail(email), ...pendingFacts });
  pendingFacts = {};
}

export type AccountFacts = {
  sandboxOrganisationId?: string;
  liveOrganisationId?: string;
  role?: string;
  hasLiveOrg?: boolean;
  testMode?: boolean;
};

export function accountFactsToPersonProperties(facts: AccountFacts): Properties {
  const props: Properties = {};
  if (facts.sandboxOrganisationId) props.sandboxOrganisationId = facts.sandboxOrganisationId;
  if (facts.liveOrganisationId) props.liveOrganisationId = facts.liveOrganisationId;
  if (facts.role) props.role = facts.role;
  if (facts.hasLiveOrg !== undefined) props.hasLiveOrg = facts.hasLiveOrg;
  return props;
}

/**
 * From the portal shell: organisation facts on the person, and the current view on every
 * event. The shell's effect can run before the user is identified (child effects run first),
 * so the facts are held and sent with identify in that case.
 */
export function setAccountFacts(facts: AccountFacts): void {
  if (!analyticsEnabled()) return;
  if (facts.testMode !== undefined) currentView = facts.testMode ? 'test' : 'live';
  const props = accountFactsToPersonProperties(facts);
  if (Object.keys(props).length === 0) return;
  if (identifiedAs) {
    sendFactsIfChanged(props);
  } else {
    pendingFacts = { ...pendingFacts, ...props };
  }
}

/** On sign-out: the next events are anonymous again. */
export function resetAnalytics(): void {
  if (!analyticsEnabled()) return;
  identifiedAs = undefined;
  pendingFacts = {};
  writeSentFacts('');
  currentView = undefined;
  posthog.reset();
}
